// src/contexts/orcamentos/OrcamentoContext.js
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';
import * as service from '../../services/orcamentos/OrcamentoService';

const OrcamentoContext = createContext();
export const useOrcamento = () => useContext(OrcamentoContext);

export const OrcamentoProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [orcamentos, setOrcamentos] = useState([]);
  const { showAlert } = useAlert();
  const { showMessage } = useMessage();

  const executar = useCallback(async (acao, sucesso) => {
    setLoading(true);
    try {
      const resultado = await acao();
      if (sucesso) showAlert(sucesso);
      return resultado;
    } catch (error) {
      showMessage(error?.message || 'Não foi possível concluir a operação.', 'danger');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showAlert, showMessage]);

  const listar = useCallback(async filtros => {
    const resultado = await executar(() => service.listarOrcamentos(filtros));
    if (resultado !== null) setOrcamentos(resultado || []);
    return resultado;
  }, [executar]);

  const imprimir = useCallback(async id => {
    const blob = await executar(() => service.obterPdfOrcamento(id));
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [executar]);

  const value = useMemo(() => ({
    loading,
    orcamentos,
    listar,
    obter: id => executar(() => service.obterOrcamentoDetalhado(id)),
    criar: dados => executar(() => service.criarOrcamento(dados), 'Orçamento criado com sucesso.'),
    atualizar: (id, dados) => executar(() => service.atualizarOrcamento(id, dados), 'Orçamento atualizado com sucesso.'),
    aprovar: (id, dados) => executar(() => service.aprovarOrcamento(id, dados), 'Orçamento aprovado e ordem de serviço criada.'),
    reprovar: (id, dados) => executar(() => service.reprovarOrcamento(id, dados), 'Orçamento reprovado.'),
    enviarWhatsApp: id => executar(() => service.enviarOrcamentoWhatsApp(id), 'Orçamento enviado para o WhatsApp.'),
    imprimir,
  }), [loading, orcamentos, listar, executar, imprimir]);

  return <OrcamentoContext.Provider value={value}>{children}</OrcamentoContext.Provider>;
};
